import React, { useState, useEffect } from 'react';
import { Building2, ChevronDown, Check } from 'lucide-react';
import { getFromLocalStorage, saveToLocalStorage } from '../mock';

const BACKEND_URL = ''; // same-origin: reverse proxy routes /api/* to backend

const BRANCH_COLORS = {
  'branch-merkez': { bg: 'bg-blue-900/40', border: 'border-blue-700/60', text: 'text-blue-400', dot: 'bg-blue-500' },
  'branch-ermat': { bg: 'bg-emerald-900/40', border: 'border-emerald-700/60', text: 'text-emerald-400', dot: 'bg-emerald-500' },
};

const defaultColors = { bg: 'bg-gray-800/60', border: 'border-gray-700/60', text: 'text-gray-400', dot: 'bg-gray-500' };

export const getBranchColor = (branchId) => BRANCH_COLORS[branchId] || defaultColors;

export const BranchBadge = ({ branchId, branchName, small }) => {
  if (!branchId || !branchName) return null;
  const c = getBranchColor(branchId);
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded ${c.bg} ${c.border} border ${c.text} ${small ? 'text-[10px]' : 'text-xs'} font-medium`}
      data-testid={`branch-badge-${branchId}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />
      {branchName}
    </span>
  );
};

const BranchSelector = ({ selectedBranchId, onBranchChange }) => {
  const [branches, setBranches] = useState([]);
  const [open, setOpen] = useState(false);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const currentUser = getFromLocalStorage('currentUser');
    setUser(currentUser);

    const loadBranches = async () => {
      try {
        const token = getFromLocalStorage('accessToken');
        const res = await fetch(`${BACKEND_URL}/api/branches`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setBranches(data.branches || []);
        }
      } catch (e) {
        console.error('Branch load error:', e);
      }
    };
    loadBranches();

    // Restore from session
    const saved = sessionStorage.getItem('selectedBranchId');
    if (saved && onBranchChange) {
      onBranchChange(saved);
    }
  }, []);

  const isGlobalAdmin = user?.isGlobalAdmin || user?.isAdmin;
  const options = isGlobalAdmin
    ? [{ id: 'all', name: 'Tum Subeler', code: 'ALL' }, ...branches]
    : branches;

  const selected = options.find(b => b.id === selectedBranchId) || options[0];

  const handleSelect = (branch) => {
    setOpen(false);
    sessionStorage.setItem('selectedBranchId', branch.id);
    if (onBranchChange) onBranchChange(branch.id);
  };

  if (options.length <= 1) return null;

  const selColors = selectedBranchId === 'all' ? defaultColors : getBranchColor(selectedBranchId);

  return (
    <div className="relative" data-testid="branch-selector">
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border ${selColors.border} ${selColors.bg} hover:opacity-90 transition-all`}
      >
        <Building2 className={`h-4 w-4 ${selColors.text}`} />
        <span className={`text-sm font-medium ${selColors.text}`}>{selected?.name || 'Sube Sec'}</span>
        <ChevronDown className={`h-3 w-3 ${selColors.text} transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute top-full mt-1 right-0 z-50 min-w-[180px] bg-gray-900 border border-gray-700 rounded-lg shadow-xl py-1 animate-in fade-in slide-in-from-top-2 duration-200">
            {options.map(b => {
              const c = b.id === 'all' ? defaultColors : getBranchColor(b.id);
              const isActive = b.id === selectedBranchId;
              return (
                <button
                  key={b.id}
                  onClick={() => handleSelect(b)}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-800 transition-colors ${isActive ? 'bg-gray-800/80' : ''}`}
                  data-testid={`branch-option-${b.id}`}
                >
                  <span className={`w-2 h-2 rounded-full ${c.dot}`} />
                  <span className={`${c.text} font-medium flex-1 text-left`}>{b.name}</span>
                  {isActive && <Check className="h-3 w-3 text-brand" />}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default BranchSelector;
